import type { WordPayload } from "./WordNode";

export const RECORD_INTO_TRANSCRIPT_REQUEST_EVENT =
  "libretto:record-into-transcript-request" as const;

export type RecordIntoTranscriptMediaKind = "audio" | "video";
export type RecordIntoTranscriptRequestSource =
  | "transcript-context-menu"
  | "transcript-empty-line-action"
  | "timeline-context-menu"
  | "script-clip-context-menu";

/**
 * A durable description of a neighboring transcript word. Lexical node keys are
 * deliberately excluded: they are regenerated whenever the transcript rerenders.
 */
export type TranscriptWordAnchor = {
  text: string;
  payload: WordPayload;
};

export type TranscriptCaretAffinity =
  | "after-previous-word"
  | "before-next-word"
  | "exact-time";

export type TranscriptCaretAnchor = {
  /** Time in the ScriptLayerClip's local/source timeline, not composition time. */
  scriptLocalSec: number;
  affinity: TranscriptCaretAffinity;
  beforeWord: TranscriptWordAnchor | null;
  afterWord: TranscriptWordAnchor | null;
};

export type RecordIntoTranscriptRequestDetail = {
  version: 1;
  requestId: string;
  requestedAtMs: number;
  source: RecordIntoTranscriptRequestSource;
  mediaKind: RecordIntoTranscriptMediaKind;
  projectId: string | null;
  compositionId: string | null;
  scriptClipId: string | null;
  anchor: TranscriptCaretAnchor;
};

export type RecordIntoTranscriptRequestInput = Pick<
  RecordIntoTranscriptRequestDetail,
  "mediaKind" | "projectId" | "compositionId" | "scriptClipId" | "anchor"
> & {
  source?: RecordIntoTranscriptRequestSource;
};

export type TranscriptWordLike = {
  text: string;
  payload: WordPayload;
};

type TimestampLike = { seconds?: number | null } | null | undefined;

export type ScriptClipRecordTimeMapper = {
  start?: TimestampLike;
  range?: [TimestampLike, TimestampLike] | TimestampLike[];
  duration?: TimestampLike;
  timelineDuration?: TimestampLike;
  scriptSegments?: Array<{
    compStartSec?: number | null;
    srcStartSec?: number | null;
    srcEndSec?: number | null;
    playbackRate?: number | null;
  }> | null;
  mapCompTimeToClipLocal?: (compositionTimeSec: number) => number | null;
  CompositionTimeToClipTime?: (compositionTimeSec: number) => number | null;
};

const finiteNumber = (value: unknown): number | null => {
  if (value == null || value === "" || typeof value === "boolean") return null;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

/**
 * Resolve the active composition identity from editor state. Older live engine
 * instances can have an empty `compositionId`, so an empty engine value must
 * never mask the selected or project-store identity.
 */
export const resolveRecordIntoTranscriptCompositionId = (args: {
  selectedCompositionId?: unknown;
  engineCompositionId?: unknown;
  projectCompositionId?: unknown;
}): string => {
  for (const candidate of [
    args.selectedCompositionId,
    args.engineCompositionId,
    args.projectCompositionId,
  ]) {
    const value = String(candidate ?? "").trim();
    if (value) return value;
  }
  return "";
};

/**
 * Map an absolute composition time onto the ScriptClip source spine.
 *
 * Inserted gaps are composition-time holes and therefore return null from the
 * normal mapper. They anchor to the source edge immediately after the hole.
 * The final edge is handled explicitly because ScriptClip segment mapping is
 * intentionally half-open.
 */
export const resolveScriptLocalTimeForRecording = (
  clip: ScriptClipRecordTimeMapper | null | undefined,
  requestedCompositionSec: number,
): number | null => {
  if (!clip) return null;
  const requested = finiteNumber(requestedCompositionSec);
  if (requested == null) return null;

  const EPS = 1e-3;
  const clipStartSec = finiteNumber(clip.start?.seconds) ?? 0;
  const clipOutSec =
    finiteNumber(clip.range?.[1]?.seconds) ??
    finiteNumber(clip.duration?.seconds);
  const timelineDurationSec = finiteNumber(clip.timelineDuration?.seconds);
  const timelineEndAbs =
    timelineDurationSec == null ? null : clipStartSec + timelineDurationSec;

  if (requested < clipStartSec - EPS) return null;
  if (timelineEndAbs != null && requested > timelineEndAbs + EPS) return null;

  const hasDirectMapper = typeof clip.mapCompTimeToClipLocal === "function";
  if (hasDirectMapper) {
    try {
      const mapped = finiteNumber(clip.mapCompTimeToClipLocal!(requested));
      if (mapped != null) return mapped;
    } catch {}
  }

  const segments = Array.isArray(clip.scriptSegments)
    ? [...clip.scriptSegments].sort(
        (first, second) =>
          (finiteNumber(first?.compStartSec) ?? Number.POSITIVE_INFINITY) -
          (finiteNumber(second?.compStartSec) ?? Number.POSITIVE_INFINITY),
      )
    : [];

  // Some legacy/test ScriptClip shapes expose segments without the direct
  // mapper. Preserve normal playable-time mapping before treating an unmapped
  // position as an inserted hole.
  for (const segment of segments) {
    const start = finiteNumber(segment?.compStartSec);
    const sourceStart = finiteNumber(segment?.srcStartSec);
    const sourceEnd = finiteNumber(segment?.srcEndSec);
    const rateRaw = finiteNumber(segment?.playbackRate) ?? 1;
    const rate = rateRaw > 0 ? rateRaw : 1;
    if (start == null || sourceStart == null || sourceEnd == null) continue;

    const sourceDuration = Math.max(0, sourceEnd - sourceStart);
    const end = start + sourceDuration / rate;
    if (requested >= start && requested < end) {
      return Math.min(sourceEnd, sourceStart + (requested - start) * rate);
    }
  }

  // A hole between two segments represents inserted time at the next source edge.
  const nextSegment = segments.find((segment) => {
    const start = finiteNumber(segment?.compStartSec);
    return start != null && start > requested + EPS;
  });
  const nextSourceStart = finiteNumber(nextSegment?.srcStartSec);
  if (nextSourceStart != null) return nextSourceStart;

  let lastPlayableEndAbs = clipStartSec;
  for (const segment of segments) {
    const start = finiteNumber(segment?.compStartSec);
    const sourceStart = finiteNumber(segment?.srcStartSec);
    const sourceEnd = finiteNumber(segment?.srcEndSec);
    const rateRaw = finiteNumber(segment?.playbackRate) ?? 1;
    const rate = rateRaw > 0 ? rateRaw : 1;
    if (start == null || sourceStart == null || sourceEnd == null) continue;
    lastPlayableEndAbs = Math.max(
      lastPlayableEndAbs,
      start + Math.max(0, sourceEnd - sourceStart) / rate,
    );
  }

  const effectiveTimelineEndAbs = timelineEndAbs ?? lastPlayableEndAbs;
  if (
    clipOutSec != null &&
    requested >= lastPlayableEndAbs - EPS &&
    requested <= effectiveTimelineEndAbs + EPS
  ) {
    return clipOutSec;
  }

  // Legacy ScriptClip implementations may not expose the direct segment mapper.
  if (!hasDirectMapper && typeof clip.CompositionTimeToClipTime === "function") {
    try {
      return finiteNumber(clip.CompositionTimeToClipTime(requested));
    } catch {}
  }

  return null;
};

/** Clone the serializable WordNode identity/timing payload before it enters React state. */
export const snapshotTranscriptWordAnchor = (
  word: TranscriptWordLike | null | undefined,
): TranscriptWordAnchor | null => {
  if (!word) return null;
  return {
    text: String(word.text ?? ""),
    payload: { ...(word.payload ?? {}) },
  };
};

/**
 * Resolve a collapsed caret to one exact between-word edge. The requested edge
 * wins when both adjacent words exist, so silence between words is not averaged
 * away. The opposite word is only a fallback for incomplete legacy payloads.
 */
export const resolveTranscriptCaretAnchor = (args: {
  beforeWord?: TranscriptWordLike | null;
  afterWord?: TranscriptWordLike | null;
  affinity: Exclude<TranscriptCaretAffinity, "exact-time">;
}): TranscriptCaretAnchor | null => {
  const beforeWord = snapshotTranscriptWordAnchor(args.beforeWord);
  const afterWord = snapshotTranscriptWordAnchor(args.afterWord);
  if (!beforeWord && !afterWord) return null;

  const previousEdge = beforeWord
    ? finiteNumber(beforeWord.payload.endSec) ??
      finiteNumber(beforeWord.payload.startSec)
    : null;
  const nextEdge = afterWord
    ? finiteNumber(afterWord.payload.startSec) ??
      finiteNumber(afterWord.payload.endSec)
    : null;

  const resolved =
    args.affinity === "before-next-word"
      ? nextEdge ?? previousEdge
      : previousEdge ?? nextEdge;
  if (resolved == null) return null;

  return {
    scriptLocalSec: Math.max(0, resolved),
    affinity: args.affinity,
    beforeWord,
    afterWord,
  };
};

/**
 * Build the same stable anchor for a timeline click. Exact positions in silence
 * remain exact; a position strictly inside a timed word snaps to its nearest edge.
 */
export const resolveTimelineRecordAnchor = (
  requestedScriptLocalSec: number,
  words: readonly TranscriptWordLike[],
): TranscriptCaretAnchor | null => {
  const requested = finiteNumber(requestedScriptLocalSec);
  if (requested == null) return null;
  const scriptLocalSec = Math.max(0, requested);

  const timedWords = words.map((word, index) => {
    const startSec = finiteNumber(word.payload.startSec);
    const rawEndSec =
      finiteNumber(word.payload.endSec) ?? startSec;
    return {
      word,
      index,
      startSec,
      endSec:
        startSec != null && rawEndSec != null
          ? Math.max(startSec, rawEndSec)
          : rawEndSec,
    };
  });

  const wordBefore = (edgeSec: number): TranscriptWordLike | null => {
    let match: (typeof timedWords)[number] | null = null;
    for (const timed of timedWords) {
      if (timed.endSec == null || timed.endSec > edgeSec) continue;
      if (
        !match ||
        timed.endSec > (match.endSec ?? Number.NEGATIVE_INFINITY) ||
        (timed.endSec === match.endSec && timed.index > match.index)
      ) {
        match = timed;
      }
    }
    return match?.word ?? null;
  };

  const wordAfter = (edgeSec: number): TranscriptWordLike | null => {
    let match: (typeof timedWords)[number] | null = null;
    for (const timed of timedWords) {
      if (timed.startSec == null || timed.startSec < edgeSec) continue;
      if (
        !match ||
        timed.startSec < (match.startSec ?? Number.POSITIVE_INFINITY) ||
        (timed.startSec === match.startSec && timed.index < match.index)
      ) {
        match = timed;
      }
    }
    return match?.word ?? null;
  };

  const containing = timedWords.filter(
    ({ startSec, endSec }) =>
      startSec != null &&
      endSec != null &&
      scriptLocalSec > startSec &&
      scriptLocalSec < endSec,
  );

  if (containing.length > 0) {
    // Treat transitively overlapping words as one occupied interval. Snapping to
    // an interior edge of one word could still split another speaker's word.
    let occupiedStart = Math.min(...containing.map(({ startSec }) => startSec!));
    let occupiedEnd = Math.max(...containing.map(({ endSec }) => endSec!));
    let expanded = true;
    while (expanded) {
      expanded = false;
      for (const timed of timedWords) {
        if (
          timed.startSec == null ||
          timed.endSec == null ||
          timed.endSec <= timed.startSec ||
          timed.startSec >= occupiedEnd ||
          timed.endSec <= occupiedStart
        ) {
          continue;
        }
        const nextStart = Math.min(occupiedStart, timed.startSec);
        const nextEnd = Math.max(occupiedEnd, timed.endSec);
        if (nextStart !== occupiedStart || nextEnd !== occupiedEnd) {
          occupiedStart = nextStart;
          occupiedEnd = nextEnd;
          expanded = true;
        }
      }
    }

    const snapToStart =
      scriptLocalSec - occupiedStart <= occupiedEnd - scriptLocalSec;
    const snappedSec = snapToStart ? occupiedStart : occupiedEnd;
    return {
      scriptLocalSec: snappedSec,
      affinity: snapToStart ? "before-next-word" : "after-previous-word",
      beforeWord: snapshotTranscriptWordAnchor(wordBefore(snappedSec)),
      afterWord: snapshotTranscriptWordAnchor(wordAfter(snappedSec)),
    };
  }

  let previous: TranscriptWordLike | null = null;
  let previousEnd = Number.NEGATIVE_INFINITY;
  let next: TranscriptWordLike | null = null;
  let nextStart = Number.POSITIVE_INFINITY;
  for (const timed of timedWords) {
    if (
      timed.endSec != null &&
      timed.endSec <= scriptLocalSec &&
      timed.endSec >= previousEnd
    ) {
      previous = timed.word;
      previousEnd = timed.endSec;
    }
    if (
      timed.startSec != null &&
      timed.startSec >= scriptLocalSec &&
      timed.startSec <= nextStart
    ) {
      next = timed.word;
      nextStart = timed.startSec;
    }
  }

  return {
    scriptLocalSec,
    affinity: "exact-time",
    beforeWord: snapshotTranscriptWordAnchor(previous),
    afterWord: snapshotTranscriptWordAnchor(next),
  };
};

const createRequestId = (): string => {
  const cryptoWithUuid = globalThis.crypto as
    | (Crypto & { randomUUID?: () => string })
    | undefined;
  return (
    cryptoWithUuid?.randomUUID?.() ??
    `record_${Date.now()}_${Math.random().toString(36).slice(2)}`
  );
};

export const createRecordIntoTranscriptRequest = (
  input: RecordIntoTranscriptRequestInput,
  metadata: { requestId?: string; requestedAtMs?: number } = {},
): RecordIntoTranscriptRequestDetail => ({
  version: 1,
  requestId: metadata.requestId ?? createRequestId(),
  requestedAtMs: metadata.requestedAtMs ?? Date.now(),
  source: input.source ?? "transcript-context-menu",
  mediaKind: input.mediaKind,
  projectId: input.projectId,
  compositionId: input.compositionId,
  scriptClipId: input.scriptClipId,
  anchor: input.anchor,
});

/** Dispatch the one browser-level handoff consumed by the editor orchestrator. */
export const dispatchRecordIntoTranscriptRequest = (
  input: RecordIntoTranscriptRequestInput,
  target: EventTarget = window,
): RecordIntoTranscriptRequestDetail => {
  const detail = createRecordIntoTranscriptRequest(input);
  target.dispatchEvent(
    new CustomEvent<RecordIntoTranscriptRequestDetail>(
      RECORD_INTO_TRANSCRIPT_REQUEST_EVENT,
      { detail },
    ),
  );
  return detail;
};

declare global {
  interface WindowEventMap {
    "libretto:record-into-transcript-request": CustomEvent<RecordIntoTranscriptRequestDetail>;
  }
}
